import { ApolloClient, InMemoryCache, split } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createUploadLink } from "apollo-upload-client";
import { createClient } from "graphql-ws";
import {
  getValidStoredToken,
  isAuthenticationFailure,
  notifyInvalidSession,
} from "./utils/authSession";
import {
  REALTIME_ACK_TIMEOUT_MS,
  REALTIME_KEEP_ALIVE_MS,
  REALTIME_RETRY_ATTEMPTS,
  waitForRealtimeRetry,
} from "./utils/realtimeClientPolicy";
import {
  REALTIME_CONNECTION_STATES,
  setRealtimeConnectionState,
} from "./utils/notificationRecovery";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5005";
const WS_BASE_URL = API_BASE_URL.replace(/^http/, "ws");

// 1️⃣ Upload-enabled link to your GraphQL endpoint
const uploadLink = createUploadLink({
  uri: `${API_BASE_URL}/graphql`,
  credentials: "include", // Required for cookies
  headers: {
    "x-apollo-operation-name": "UploadAvatar", // or just a non-empty string
  },
});

// 2️⃣ Auth link to inject JWT from localStorage
const authLink = setContext((_, { headers }) => {
  const token = getValidStoredToken({ notifyOnExpiration: true });
  return {
    authSessionToken: token,
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: `${WS_BASE_URL}/graphql`,
    connectionParams: () => {
      const token = getValidStoredToken({ notifyOnExpiration: true });
      return {
        authorization: token ? `Bearer ${token}` : "",
      };
    },
    retryAttempts: REALTIME_RETRY_ATTEMPTS,
    retryWait: waitForRealtimeRetry,
    keepAlive: REALTIME_KEEP_ALIVE_MS,
    connectionAckWaitTimeout: REALTIME_ACK_TIMEOUT_MS,
    on: {
      connecting: () => setRealtimeConnectionState(REALTIME_CONNECTION_STATES.CONNECTING),
      connected: () => setRealtimeConnectionState(REALTIME_CONNECTION_STATES.CONNECTED),
      closed: () => setRealtimeConnectionState(REALTIME_CONNECTION_STATES.DISCONNECTED),
      error: () => setRealtimeConnectionState(REALTIME_CONNECTION_STATES.DISCONNECTED),
    },
  })
);

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const statusCode =
    networkError?.statusCode ||
    networkError?.status ||
    networkError?.response?.status;

  const requestToken = operation?.getContext?.()?.authSessionToken;

  // A generic 403 is an authorization denial, not proof that the login is
  // invalid. A 401 or an explicit GraphQL authentication error is handled,
  // and only when the failed operation used the currently stored token.
  if (isAuthenticationFailure({ graphQLErrors, statusCode }) && requestToken) {
    notifyInvalidSession("graphql-auth-failed", undefined, requestToken);
  }
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  authLink.concat(uploadLink)
);

// 3️⃣ Apollo Client setup
const client = new ApolloClient({
  link: errorLink.concat(splitLink),
  cache: new InMemoryCache({
    typePolicies: {
      Church: {
        fields: {
          publicSite: { merge: true },
        },
      },
    },
  }),
});

export default client;
